import React, { useEffect, useMemo } from 'react';
import { Button, Dropdown, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import useAvailableExportFormats from 'packages/hooks/useAvailableExportFormats';
import { FormSectionContent } from 'packages/pages/editor/RightSideMenu/index.styled';
import { setSaveStatus } from 'packages/store/creativeEditor/creativeEditor.actions';
import useEditor from '~/hooks/useEditor';
import generateTestId from '~/helpers/testIdHelpers';

export const ExportFormats = () => {
    const dispatch = useThunkDispatch();
    const { translate } = useTranslate();
    const { creativeType, exportConfigurationIds, setExportConfigurationIds } = useEditor();
    const { isLoadingExportFormats, availableExportFormats } = useAvailableExportFormats({
        creativeType,
    });

    const selectedCount = exportConfigurationIds.length;
    const testId = generateTestId('global_export_formats');

    const availableFormatIds = useMemo(
        () => new Set(availableExportFormats.map(({ id }) => id)),
        [availableExportFormats],
    );

    useEffect(() => {
        if (isLoadingExportFormats) {
            return;
        }

        const nextSelectedIds = exportConfigurationIds.filter((id) => availableFormatIds.has(id));

        if (nextSelectedIds.length !== exportConfigurationIds.length) {
            setExportConfigurationIds(nextSelectedIds);
            dispatch(setSaveStatus(false));
        }
    }, [availableFormatIds, dispatch, isLoadingExportFormats, exportConfigurationIds, setExportConfigurationIds]);

    const toggleExportFormat = (id: number) => {
        const nextSelectedIds = exportConfigurationIds.includes(id)
            ? exportConfigurationIds.filter((selectedId) => selectedId !== id)
            : [...exportConfigurationIds, id];

        setExportConfigurationIds(nextSelectedIds);
        dispatch(setSaveStatus(false));
    };

    const triggerText = isLoadingExportFormats
        ? translate('modal.quick-export.loading')
        : selectedCount
          ? translate('editor.export-formats.selected', { count: selectedCount })
          : translate('editor.export-formats.all-formats');
    const isDisabled = isLoadingExportFormats || !availableExportFormats.length;

    return (
        <FormSectionContent onSubmit={(event) => event.preventDefault()}>
            <Form.Group>
                <Form.Label>{translate('editor.export-formats.label')}</Form.Label>
                <Dropdown
                    trigger={({ isOpen, ...triggerProps }) => (
                        <Button
                            isPressed={isOpen}
                            rightIcon={<Dropdown.Arrow />}
                            isFullWidth
                            isDisabled={isDisabled}
                            {...triggerProps}
                            {...testId}
                        >
                            <Dropdown.SelectButtonText>{triggerText}</Dropdown.SelectButtonText>
                        </Button>
                    )}
                >
                    {availableExportFormats.map(({ id, displayName, description }) => (
                        <Dropdown.ListItem
                            key={id}
                            isChecked={exportConfigurationIds.includes(id)}
                            onClick={() => toggleExportFormat(id)}
                            subtext={description}
                        >
                            {displayName}
                        </Dropdown.ListItem>
                    ))}
                </Dropdown>
                <Form.HelperText>{translate('editor.export-formats.helper-text')}</Form.HelperText>
            </Form.Group>
        </FormSectionContent>
    );
};
